import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useJ5M1, ItemState } from './hooks/useJ5M1';
import { j5m1Api } from '../../services/j5m1Api';
import { BatchAcceptanceBridge, pwj5m1f1AcceptanceManifest } from '../../features/acceptance';
import './J5M1Form.css';

const fmtDate = (v: string | null | undefined): string => {
  if (!v) return '';
  const d = dayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD') : '';
};

const J5M1Form: React.FC = () => {
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');

  const {
    items,
    loading,
    executeLoading,
    queried,
    selectedCount,
    handleQuery,
    handleToggleItem,
    handleExecute,
  } = useJ5M1();

  const onQuery = useCallback(() => {
    handleQuery(startNo, endNo);
  }, [handleQuery, startNo, endNo]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  const onExecute = useCallback(() => {
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷已勾選的 ${selectedCount} 筆樣品申請單？`,
      okText: '確定沖銷',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        await handleExecute();
      },
    });
  }, [selectedCount, handleExecute]);

  const columns: ColumnsType<ItemState> = [
    {
      title: '沖銷',
      dataIndex: 'selected',
      key: 'selected',
      width: 60,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) =>
            handleToggleItem(record.item.fj110_01, e.target.checked)
          }
        />
      ),
    },
    {
      title: '申請單號',
      dataIndex: ['item', 'fj110_01'],
      key: 'fj110_01',
      width: 120,
    },
    {
      title: '申請日期',
      dataIndex: ['item', 'fj110_11'],
      key: 'fj110_11',
      width: 120,
      render: (v: string | null) => fmtDate(v),
    },
    {
      title: '部門',
      dataIndex: ['item', 'fj110_22'],
      key: 'fj110_22',
      width: 140,
    },
    {
      title: '客戶',
      dataIndex: ['item', 'fj110_12'],
      key: 'fj110_12',
      width: 180,
    },
    {
      title: '業務員',
      dataIndex: ['item', 'fj110_21'],
      key: 'fj110_21',
      width: 120,
    },
  ];

  return (
    <div className="j5m1-form">
      {(loading || executeLoading) && (
        <div className="loading-overlay">
          {executeLoading ? '沖銷執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>研發管理</a>
              <span>/</span>
              <span className="current">樣品申請單核准沖銷</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>樣品申請單核准沖銷</h1>
            <span className="badge">J5M1</span>
          </div>
        </div>

        <div className="query-bar">
          <label data-contract-field="fj110_01">申請單號 .從:</label>
          <Input
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 180 }}
            allowClear
          />
          <span className="separator">至</span>
          <Input
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 180 }}
            allowClear
          />
          <Button type="primary" onClick={onQuery} loading={loading}>
            確定範圍
          </Button>
        </div>

        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>，已勾選 <strong>{selectedCount}</strong> 筆</span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.fj110_01}
              pagination={false}
              size="small"
              scroll={{ y: 400 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請輸入申請單號範圍並點擊「確定範圍」查詢
            </div>
          )}
        </div>

        <div className="action-bar">
          <Button
            type="primary"
            size="large"
            disabled={selectedCount === 0 || executeLoading}
            loading={executeLoading}
            onClick={onExecute}
          >
            存檔 ({selectedCount})
          </Button>
          <Button danger size="large" onClick={() => navigate('/erp')}>
            離開
          </Button>
        </div>
        <BatchAcceptanceBridge
          manifest={pwj5m1f1AcceptanceManifest}
          records={items}
          selectedKeys={items.filter((row) => row.selected).map((row) => row.item.fj110_01)}
          keyOf={(row) => row.item.fj110_01}
          queryExact={(key) => j5m1Api.query(key, key)}
          lastApiResponse={items}
          loading={loading || executeLoading}
          onAutoFill={() => items[0] && handleToggleItem(items[0].item.fj110_01, true)}
          autoFillDisabled={items.length === 0}
          screenDraft={{ startNo, endNo }}
        />
      </div>
    </div>
  );
};

export default J5M1Form;
